"use client";

import { LocaleLink } from "@i18n/routing";
import type { WorkflowStepSignal } from "@repo/core/client";
import { StepSignal } from "@ui/components/step-signal";
import { ArrowRightIcon } from "lucide-react";

/**
 * "How this step works in Spyglasses" aside for workflow how-to posts. The
 * article's steps stay generic; this card carries the product connection so
 * it's present without interrupting the main walkthrough. The signal bento
 * reuses the shared StepSignal primitive (the same visual the workflow
 * runner uses), fed with illustrative data from MDX props.
 */
export function WorkflowCallout({
	title,
	page,
	href,
	signal,
	signalCaption,
	children,
}: {
	/** Short label for what Spyglasses does in this step. */
	title: string;
	/** The dashboard the step happens on (e.g. "Key Messages"). */
	page: string;
	/** Product or docs page to link to. */
	href: string;
	/** Optional bento: a StepSignal spec rendered as the completion signal. */
	signal?: WorkflowStepSignal;
	/** Label above the bento (defaults to "Completion signal"). */
	signalCaption?: string;
	children: React.ReactNode;
}) {
	return (
		<aside className="not-prose my-8 rounded-xl border bg-card p-5">
			<div className="flex items-center gap-2">
				<span className="text-xs font-bold uppercase tracking-wide text-primary">
					In Spyglasses
				</span>
				<span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">
					{page}
				</span>
			</div>
			<div
				className={
					signal ? "mt-3 grid gap-5 md:grid-cols-[1fr_260px]" : "mt-3"
				}
			>
				<div>
					<p className="font-semibold">{title}</p>
					<div className="mt-1.5 space-y-2 text-sm leading-relaxed text-muted-foreground">
						{children}
					</div>
					<LocaleLink
						href={href}
						className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
					>
						See how it works
						<ArrowRightIcon className="size-3.5" aria-hidden />
					</LocaleLink>
				</div>
				{signal && (
					<div className="rounded-lg border border-dashed p-4">
						<p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
							{signalCaption ?? "Completion signal"}
						</p>
						<div className="mt-3">
							<StepSignal signal={signal} />
						</div>
					</div>
				)}
			</div>
		</aside>
	);
}
